import { format, parse } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

// Editors keep wall-clock times as browser-anchored ISO strings (pickers write
// and read in the browser's zone), while the DB stores instants encoded in the
// venue's zone. These two inverse helpers convert at the submit/hydration
// boundaries so a typed "3pm" means 3pm AT THE VENUE regardless of where the
// editor sits. Promoted from ProgramEditor/utils/dates.ts.

const YMD_FMT = 'yyyy-MM-dd';
export const YMD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// Strict parse on calendar dates avoids the UTC-shift bug from new Date('2025-07-15').
export const parseDateOrNull = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  if (YMD_PATTERN.test(value)) {
    const d = parse(value, YMD_FMT, new Date());
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Re-encode `iso`'s browser-local wall-clock as that same wall-clock in `timeZone`. */
export const browserIsoToZoneIso = (iso: string, timeZone: string | null | undefined): string => {
  if (!iso || !timeZone) return iso;
  const d = parseDateOrNull(iso);
  if (!d) return iso;
  return fromZonedTime(format(d, "yyyy-MM-dd'T'HH:mm:ss"), timeZone).toISOString();
};

/**
 * Inverse: a browser-anchored ISO whose local reading equals `iso`'s wall-clock
 * in `timeZone`. The returned string's UTC value is deliberately "wrong" — only
 * its browser-local reading (new Date(x).getHours() etc.) is meaningful, which
 * is all the pickers consume. Don't call getUTC* on it.
 */
export const zoneIsoToBrowserIso = (iso: string, timeZone: string | null | undefined): string => {
  if (!iso || !timeZone) return iso;
  const d = parseDateOrNull(iso);
  if (!d) return iso;
  return toZonedTime(d, timeZone).toISOString();
};
